import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { isForestRoute } from '../lib/routes';
import { RSVP_COMING_SOON, nav } from '../content/wedding';
import { Monogram } from './Monogram';

export function Nav() {
  const { path } = useLocation();
  const [stuck, setStuck] = useState(false);
  const [progress, setProgress] = useState(0);
  const [open, setOpen] = useState(false);
  // The RSVP routes are full forest-coloured pages; the nav has to invert over them.
  const onForest = isForestRoute(path);

  useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      const scrolled = window.scrollY;
      const span = document.documentElement.scrollHeight - window.innerHeight;
      setStuck(scrolled > 40);
      setProgress(span > 0 ? Math.min(1, scrolled / span) : 0);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  /* Arriving somewhere is the menu's whole purpose, so it closes on its own
     once the route changes rather than leaving the guest to dismiss it. */
  useEffect(() => setOpen(false), [path]);

  /* And it closes if the window grows past the width where the bar carries
     its own links: the panel is hidden from there up, and an open one left
     behind would take the page's scroll lock with it. */
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 52rem)');
    const check = () => {
      if (wide.matches) setOpen(false);
    };
    check();
    wide.addEventListener('change', check);
    return () => wide.removeEventListener('change', check);
  }, []);

  /**
   * Hold the page still behind the open menu.
   *
   * Not `overflow: hidden` on the document: that collapses the scrollport, and
   * a page with nowhere to scroll to snaps back to the top — so the menu would
   * open and the page behind it would silently lose the guest's place. The
   * body is pinned where it already was with the offset carried in a custom
   * property, and the scroll handed back on the way out. `instant` matters:
   * the page sets `scroll-behavior: smooth`, and without it the restore is a
   * visible ride back down.
   */
  useEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    const offset = window.scrollY;
    root.style.setProperty('--menu-scroll', `${offset}px`);
    root.classList.add('is-menuOpen');

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
      root.classList.remove('is-menuOpen');
      root.style.removeProperty('--menu-scroll');
      window.scrollTo({ top: offset, behavior: 'instant' });
    };
  }, [open]);

  return (
    <header
      class={`nav ${stuck ? 'is-stuck' : ''} ${onForest ? 'nav--onForest' : ''} ${
        open ? 'is-menuOpen' : ''
      }`}
    >
      <div class={`shell nav__inner ${RSVP_COMING_SOON ? 'nav__inner--noCta' : ''}`}>
        <a class="nav__mark" href="/" aria-label="Home">
          <Monogram decorative />
        </a>

        {/* Pages now rather than sections, so a link means the same thing
            wherever it is followed from and `aria-current` has something real
            to mark. */}
        <nav class="nav__links" aria-label="Pages">
          {nav.map((item) => (
            <a
              key={item.href}
              class="nav__link"
              href={item.href}
              aria-current={path === item.href ? 'page' : undefined}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* Off until invitations are out — see `comingSoon` in the content file.
            Bespoke invitation links still reach the flow directly. */}
        {!RSVP_COMING_SOON && (
          <a class={`btn nav__cta ${onForest ? 'btn--onForest' : ''}`} href="/rsvp">
            RSVP
          </a>
        )}

        {/* Below 52rem the links do not fit, and until the site was split
            across pages that left a phone with no navigation at all. */}
        <button
          class="nav__menuBtn"
          type="button"
          aria-expanded={open}
          aria-controls="nav-menu"
          onClick={() => setOpen((was) => !was)}
        >
          {open ? 'Close' : 'Menu'}
          <span class="nav__burger" aria-hidden="true" />
        </button>
      </div>

      <div class="nav__tide" style={{ '--progress': progress }} aria-hidden="true" />

      <div class="menu" id="nav-menu" hidden={!open}>
        <nav class="shell menu__links" aria-label="Pages">
          <a class="menu__link" href="/" aria-current={path === '/' ? 'page' : undefined}>
            Home
          </a>
          {nav.map((item) => (
            <a
              key={item.href}
              class="menu__link"
              href={item.href}
              aria-current={path === item.href ? 'page' : undefined}
            >
              {item.label}
            </a>
          ))}
          {!RSVP_COMING_SOON && (
            <a class="btn menu__cta" href="/rsvp">
              RSVP
            </a>
          )}
        </nav>
      </div>
    </header>
  );
}
